import {useNavigate, useParams, Link} from 'react-router-dom';
import { useState, useEffect } from 'react';
import {get} from '../helpers/httpClient';
import { usePosterColors } from '../PosterContext';
import GalleryRenderer from '../helpers/renderer/galleryRenderer';
import GalleryScaledRenderer from '../helpers/renderer/galleryScaledRenderer';
import InquiryFormRenderer from '../helpers/renderer/inquiryFormRenderer';
import Catalog from './component/catalog';
import Article from './component/article';
import Gallery from './component/gallery';
import GalleryItem from './component/galleryItem';
import Poster from './component/poster';

const pageCache = {};

function App() {
    const params = useParams();
    const navigate = useNavigate();
    const { setPosterColors } = usePosterColors();

    const slug = params['*'] || '';
    const [page, setPage] = useState(pageCache[slug]?.page || null);
    const [breadcrumbs, setBreadcrumbs] = useState(pageCache[slug]?.breadcrumbs || []);

    const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 768);

    useEffect(() => {
        const handleResize = () => setIsLargeScreen(window.innerWidth > 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    useEffect(() => {
        const getPage = async () => {
            try {
                const response = await get('pages?slug=' + slug);
                if (response) {
                    pageCache[slug] = { page: response.page, breadcrumbs: response.breadcrumbs || [] };
                    setPage(response.page);
                    setBreadcrumbs(response.breadcrumbs || []);
                }
            } catch (error) {
                navigate('/404')
            }
        };

        getPage();
    }, [slug]);

    useEffect(() => {
        if (page?.title) {
            document.title = page.title;
        }
    }, [page?.title]);

    useEffect(() => {
        if (page?.type === 'poster') {
            document.body.classList.add('poster-page');
            const { overlayColor, textShadeColor } = page.extra || {};
            setPosterColors({ overlayColor, textShadeColor });
            return () => {
                document.body.classList.remove('poster-page');
                setPosterColors(null);
            };
        }
    }, [page?.type]);

    useEffect(() => {
        if (page?.type !== 'poster') return;
        const { background, backgroundVertical, backgroundType } = page.extra || {};
        if (backgroundType !== 'image') return;

        const links = [];
        const addLink = (href, media) => {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = href;
            if (media) link.media = media;
            document.head.appendChild(link);
            links.push(link);
        };

        if (background && backgroundVertical) {
            addLink(background, '(orientation: landscape)');
            addLink(backgroundVertical, '(orientation: portrait)');
        } else if (background || backgroundVertical) {
            addLink(background || backgroundVertical);
        }

        return () => links.forEach(l => l.remove());
    }, [page?.extra?.background, page?.extra?.backgroundVertical, page?.extra?.backgroundType]);

    if (!page) return null;

    if (page.type === 'poster') {
        return <Poster info={page} />;
    }


    const components = page.components?.items || [];
    const containerHeight = 32 * 16;

    const renderComponent = (comp, idx) => {
        if (comp.type === 'inquiry_form') return <InquiryFormRenderer key={idx} comp={comp} />;
        return <GalleryRenderer key={idx} comp={comp} />;
    };
    const renderScaledComponent = (comp, idx) => {
        if (comp.type === 'inquiry_form') return <InquiryFormRenderer key={idx} comp={comp} />;
        return <GalleryScaledRenderer key={idx} comp={comp} containerHeight={containerHeight} isLargeScreen={isLargeScreen} />;
    };

    const getComponentsForPosition = (pos) => {
        return components.filter(comp => {
            const currentPos = isLargeScreen ? comp.positionWideScreen : comp.positionSmallScreen;
            return currentPos === pos;
        });
    };

    const topComponents = getComponentsForPosition('top');
    const bottomComponents = getComponentsForPosition('bottom');
    const leftComponents = getComponentsForPosition('left');
    const rightComponents = getComponentsForPosition('right');

    return (
        <div key={page.slug} className="page-container page-appear">
            <div className="page-layout">
                {breadcrumbs.length > 1 && (
                    <nav className="breadcrumbs">
                        {breadcrumbs.map((crumb, idx) => {
                            const path = new URL(crumb.url).pathname;
                            const isLast = idx === breadcrumbs.length - 1;
                            const isMobileHidden = idx < breadcrumbs.length - 2;
                            return (
                                <span key={idx} className={isMobileHidden ? 'breadcrumb-hidden-mobile' : ''}>
                                    {idx > 0 && <span className="breadcrumb-sep">›</span>}
                                    {isLast
                                        ? <span className="breadcrumb-current">{crumb.name}</span>
                                        : <Link to={path}>{crumb.name}</Link>
                                    }
                                </span>
                            );
                        })}
                    </nav>
                )}
                <div className={"page-top"}>
                    {topComponents.map(renderScaledComponent)}
                </div>

                <div className="page-main-wrapper">
                    {isLargeScreen && leftComponents.length > 0 && (
                        <div className="page-side page-left">
                            {leftComponents.map(renderComponent)}
                        </div>
                    )}

                    <div className="page-center">
                        <div className="d-flex justify-content-center">
                            {page.type === 'article' && <Article info={page} />}
                            {page.type === 'catalog' && <Catalog info={page} />}
                            {page.type === 'gallery' && <Gallery info={page} />}
                            {page.type === 'galleryitem' && <GalleryItem info={page} isMobile={!isLargeScreen} />}
                        </div>
                    </div>

                    {isLargeScreen && rightComponents.length > 0 && (
                        <div className="page-side page-right">
                            {rightComponents.map(renderComponent)}
                        </div>
                    )}

                    {!isLargeScreen && [...leftComponents, ...rightComponents].map(renderComponent)}
                </div>

                <div className={"page-bottom"}>
                    {bottomComponents.map(renderScaledComponent)}
                </div>
            </div>
        </div>
    );
}

export default App;